import { useEffect, useState } from "react";
import {
  deleteUnmatched,
  fetchCompanies,
  fetchUnmatched,
  promoteUnmatched,
  rescanPayloads,
  type Company,
  type Unmatched,
  type UnmatchedAccount,
  type UnmatchedLoan,
} from "../api";
import { formatIban, formatMoney } from "../lib/grouping";

export function UnmatchedView() {
  const [items, setItems] = useState<Unmatched[] | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [rescanning, setRescanning] = useState(false);

  const load = async () => {
    setError(null);
    try {
      const [u, c] = await Promise.all([fetchUnmatched(), fetchCompanies()]);
      setItems(u.items);
      setCompanies(c.items);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => { void load(); }, []);

  const onRescan = async () => {
    setRescanning(true);
    try {
      const r = await rescanPayloads();
      alert(
        `Rescan terminado.\nPayloads procesados: ${r.payloadsProcessed}\n` +
          `Cuentas sincronizadas: ${r.accounts.synced} · sin vincular: ${r.accounts.unmatched}\n` +
          `Préstamos sincronizados: ${r.loans.synced} · sin vincular: ${r.loans.unmatched}\n` +
          (r.errors.length ? `Errores: ${r.errors.length}` : "Sin errores"),
      );
      load();
    } catch (e: unknown) {
      alert(`Error en rescan: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setRescanning(false);
    }
  };

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h2>Sin vincular</h2>
          <p className="meta">
            Cuentas y préstamos que han llegado en payloads del banco pero no
            están dados de alta en tus tablas. Puedes "Crear" para promocionar
            la entrada a una cuenta/préstamo real (te pre-rellena los datos),
            o "Ignorar" si no la quieres.
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button type="button" onClick={onRescan} disabled={rescanning}>
            {rescanning ? "Procesando…" : "↻ Reescanear payloads"}
          </button>
          <button type="button" onClick={load}>Recargar</button>
        </div>
      </div>

      {error && <p className="error">{error}</p>}
      {items === null && !error && <p>Cargando…</p>}
      {items && items.length === 0 && (
        <p className="meta">No hay nada sin vincular. ¡Bien!</p>
      )}

      {items && items.length > 0 && (
        <table className="data">
          <thead>
            <tr>
              <th>Tipo</th>
              <th>Banco</th>
              <th>Identificador</th>
              <th>Nombre / Concepto</th>
              <th className="num">Importe</th>
              <th>Empresa sugerida</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((u) =>
              u.kind === "account" ? (
                <UnmatchedAccountRow key={u.id} u={u} companies={companies} onUpdated={load} />
              ) : (
                <UnmatchedLoanRow key={u.id} u={u} companies={companies} onUpdated={load} />
              ),
            )}
          </tbody>
        </table>
      )}
    </section>
  );
}

function PromoteCell({
  id,
  defaultCompanyId,
  companies,
  onUpdated,
}: {
  id: string;
  /** Id ya resuelto al id real de la empresa (si hay externalId match);
   *  si no, vacío para forzar selección manual. */
  defaultCompanyId: string;
  companies: Company[];
  onUpdated: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [companyId, setCompanyId] = useState(defaultCompanyId);
  const [alias, setAlias] = useState("");
  const [bankName, setBankName] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!companyId) {
      alert("Selecciona una empresa.");
      return;
    }
    setBusy(true);
    try {
      await promoteUnmatched(id, { companyId, alias: alias || undefined, bankName: bankName || undefined });
      onUpdated();
    } catch (e: unknown) {
      alert(`Error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const onIgnore = async () => {
    if (!confirm("¿Ignorar esta entrada? Volverá a aparecer si llega otro payload con la misma cuenta/préstamo.")) return;
    setBusy(true);
    try {
      await deleteUnmatched(id);
      onUpdated();
    } catch (e: unknown) {
      alert(`Error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <span className="actions">
        <button type="button" onClick={() => setOpen(true)} disabled={busy || companies.length === 0}>
          Crear
        </button>
        <button type="button" className="danger" onClick={onIgnore} disabled={busy}>Ignorar</button>
      </span>
    );
  }
  return (
    <span style={{ display: "inline-flex", gap: "0.3rem", flexWrap: "wrap" }}>
      <select value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
        <option value="">— Empresa —</option>
        {companies.map((c) => (
          <option key={c.id} value={c.id}>{c.alias || c.name}</option>
        ))}
      </select>
      <input placeholder="Alias" value={alias} onChange={(e) => setAlias(e.target.value)} style={{ width: "8rem" }} />
      <input placeholder="Banco" value={bankName} onChange={(e) => setBankName(e.target.value)} style={{ width: "8rem" }} />
      <button type="button" onClick={save} disabled={busy}>OK</button>
      <button type="button" onClick={() => setOpen(false)} disabled={busy}>✕</button>
    </span>
  );
}

function SuggestionCell({ u }: { u: UnmatchedAccount | UnmatchedLoan }) {
  if (u.resolvedCompanyId) {
    return (
      <span>
        <span className="badge ok">✓ {u.resolvedCompanyName || u.resolvedCompanyId}</span>
        <span className="meta" style={{ marginLeft: "0.4rem", fontSize: "0.85em" }}>
          (banco: <code>{u.suggestedCompanyId}</code>)
        </span>
      </span>
    );
  }
  return (
    <span>
      {u.suggestedCompanyName || <span className="meta">—</span>}
      {u.suggestedCompanyId && <> · <code>{u.suggestedCompanyId}</code></>}
      {u.suggestedCompanyId && (
        <span className="meta" style={{ marginLeft: "0.4rem", fontSize: "0.85em" }}>
          (sin empresa con este ID)
        </span>
      )}
    </span>
  );
}

function UnmatchedAccountRow({
  u,
  companies,
  onUpdated,
}: {
  u: UnmatchedAccount;
  companies: Company[];
  onUpdated: () => void;
}) {
  return (
    <tr>
      <td><span className="badge">cuenta</span></td>
      <td><code>{u.bankCode}</code></td>
      <td><code>{formatIban(u.iban)}</code></td>
      <td>{u.bankAccountName || <span className="meta">—</span>}</td>
      <td className="num">{formatMoney(u.balanceAvailable, u.currency)}</td>
      <td><SuggestionCell u={u} /></td>
      <td>
        <PromoteCell
          id={u.id}
          defaultCompanyId={u.resolvedCompanyId}
          companies={companies}
          onUpdated={onUpdated}
        />
      </td>
    </tr>
  );
}

function UnmatchedLoanRow({
  u,
  companies,
  onUpdated,
}: {
  u: UnmatchedLoan;
  companies: Company[];
  onUpdated: () => void;
}) {
  return (
    <tr>
      <td><span className="badge warn">préstamo</span></td>
      <td><code>{u.bankCode}</code></td>
      <td><code>{u.contractNumber}</code></td>
      <td>{u.loanType || <span className="meta">—</span>}</td>
      <td className="num">{formatMoney(u.pendingAmount, u.currency)}</td>
      <td><SuggestionCell u={u} /></td>
      <td>
        <PromoteCell
          id={u.id}
          defaultCompanyId={u.resolvedCompanyId}
          companies={companies}
          onUpdated={onUpdated}
        />
      </td>
    </tr>
  );
}
